import React, { useState, useEffect } from 'react';
import { BarChart3, Download, Calendar, TrendingUp, Users, Activity, MessageCircle, FileText, Filter, Eye } from 'lucide-react';
import relatoriosService from '../services/relatoriosService';
import { getCurrentUserProfile } from '../services/usuariosService';

const Relatorios = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [estatisticas, setEstatisticas] = useState({});
  const [relatorioTestes, setRelatorioTestes] = useState([]);
  const [relatorioChat, setRelatorioChat] = useState([]);
  const [relatorioSessoes, setRelatorioSessoes] = useState([]);
  const [relatorioLogsAcesso, setRelatorioLogsAcesso] = useState([]);
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    alunoId: '',
    escolaId: ''
  });
  const [alunos, setAlunos] = useState([]);
  const [alunosOriginais, setAlunosOriginais] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Carregar filtros quando perfil for carregado
  useEffect(() => {
    if (currentUserProfile) {
      carregarFiltros();
    }
  }, [currentUserProfile]);
  
  // Carregar dados quando filtros estiverem prontos
  useEffect(() => {
    if (currentUserProfile) {
      // Para school admin, aguardar o filtro de escola ser definido
      if (currentUserProfile.school_adm && currentUserProfile.school_id) {
        if (filtros.escolaId === currentUserProfile.school_id) {
          console.log('🔄 [Relatórios] Carregando dados com filtro de escola para school admin');
          carregarDados();
        }
      } else {
        // Para super admin, carregar dados normalmente
        console.log('🔄 [Relatórios] Carregando dados para super admin');
        carregarDados();
      }
    }
  }, [currentUserProfile, filtros.escolaId]);

  const loadInitialData = async () => {
    try {
      // Carregar perfil do usuário primeiro
      const profileResult = await getCurrentUserProfile();
      if (profileResult.success) {
        setCurrentUserProfile(profileResult.data);
        
        // Se for school admin, definir filtro de escola imediatamente
        if (profileResult.data?.school_adm && profileResult.data?.school_id) {
          console.log('🔒 [Relatórios] Definindo filtro automático para school admin:', profileResult.data.school_id);
          setFiltros(prev => ({ ...prev, escolaId: profileResult.data.school_id }));
        }
      }
      
      // Não carregar dados aqui - será feito pelos useEffect
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    }
  };

  const carregarDados = async () => {
    setLoading(true);
    try {
      console.log('📊 [Relatórios] Carregando dados com filtros:', filtros);
      
      // Aplicar filtro de escola automaticamente para school admin
      const filtrosFinais = { ...filtros };
      if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
        filtrosFinais.escolaId = currentUserProfile.school_id;
        console.log('🔒 [Relatórios] Aplicando filtro automático de escola para school admin:', currentUserProfile.school_id);
      }
      
      const [statsResult, testesResult, chatResult, sessoesResult, logsAcessoResult] = await Promise.all([
        relatoriosService.obterEstatisticasGerais(filtrosFinais),
        relatoriosService.relatorioDesempenhoTestes(filtrosFinais),
        relatoriosService.relatorioUsoChat(filtrosFinais),
        relatoriosService.relatorioSessoes(filtrosFinais),
        relatoriosService.relatorioLogsAcesso(filtrosFinais)
      ]);

      if (statsResult.success) {
        console.log('✅ Estatísticas carregadas:', statsResult.data);
        setEstatisticas(statsResult.data);
      }
      if (testesResult.success) {
        console.log('✅ Relatório de testes carregado:', testesResult.data.length, 'registros');
        setRelatorioTestes(testesResult.data);
      }
      if (chatResult.success) {
        console.log('✅ Relatório de chat carregado:', chatResult.data.length, 'registros');
        setRelatorioChat(chatResult.data);
      }
      if (sessoesResult.success) {
        console.log('✅ Relatório de sessões carregado:', sessoesResult.data.length, 'registros');
        setRelatorioSessoes(sessoesResult.data);
      }
      if (logsAcessoResult.success) {
        console.log('✅ Relatório de logs de acesso carregado:', logsAcessoResult.data.length, 'registros');
        setRelatorioLogsAcesso(logsAcessoResult.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarFiltros = async () => {
    try {
      console.log('🔍 [Relatórios] Carregando filtros...');
      console.log('🔍 [Relatórios] Profile:', currentUserProfile);
      
      // Primeiro carregar escolas
      const escolasResult = await relatoriosService.buscarEscolas();
      
      if (escolasResult.success) {
        console.log('🏫 [Relatórios] Escolas carregadas:', escolasResult.data.length);
        
        // Filtrar escolas baseado no perfil do usuário (igual ao Alunos.js)
        if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
          console.log('🔍 [Relatórios] Filtrando escola para school admin');
          const escolaDoUsuario = escolasResult.data.filter(escola => escola.id === currentUserProfile.school_id);
          console.log('🔍 [Relatórios] Escola filtrada:', escolaDoUsuario);
          setEscolas(escolaDoUsuario);
          
          // Filtro já foi definido no loadInitialData
          console.log('🔍 [Relatórios] Filtro de escola já definido para school admin:', currentUserProfile.school_id);
        } else {
          console.log('🔍 [Relatórios] Usando todas as escolas (super admin)');
          setEscolas(escolasResult.data);
        }
      }

      // Depois carregar alunos com filtro de escola se necessário
      let escolaParaFiltro = null;
      if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
        escolaParaFiltro = currentUserProfile.school_id;
      }

      const alunosResult = await relatoriosService.buscarAlunos(escolaParaFiltro);

      if (alunosResult.success) {
        console.log('👥 [Relatórios] Alunos carregados:', alunosResult.data.length);
        setAlunosOriginais(alunosResult.data);
        setAlunos(alunosResult.data);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar filtros:', error);
    }
  };

  const aplicarFiltros = () => {
    carregarDados();
  };

  // Filtrar alunos quando escola for selecionada (igual ao Alunos.js)
  const filtrarAlunosPorEscola = async (escolaId) => {
    console.log('🔍 [Relatórios] Filtrando alunos por escola:', escolaId);
    
    if (!escolaId) {
      // Se nenhuma escola selecionada, recarregar todos os alunos (respeitando school_adm)
      let escolaParaFiltro = null;
      if (currentUserProfile?.school_adm && currentUserProfile?.school_id) {
        escolaParaFiltro = currentUserProfile.school_id;
      }
      
      const alunosResult = await relatoriosService.buscarAlunos(escolaParaFiltro);
      if (alunosResult.success) {
        setAlunos(alunosResult.data);
        console.log('📚 [Relatórios] Todos os alunos carregados:', alunosResult.data.length);
      }
    } else {
      // Buscar alunos específicos da escola selecionada
      const alunosResult = await relatoriosService.buscarAlunos(escolaId);
      if (alunosResult.success) {
        setAlunos(alunosResult.data);
        console.log('📚 [Relatórios] Alunos da escola selecionada:', alunosResult.data.length);
      }
    }
    
    // Limpar seleção de aluno se não pertence à escola
    if (filtros.alunoId) {
      const alunoSelecionado = alunosOriginais.find(aluno => aluno.id === filtros.alunoId);
      if (escolaId && alunoSelecionado && alunoSelecionado.escola_id !== escolaId) {
        console.log('🧹 [Relatórios] Limpando seleção de aluno incompatível');
        setFiltros(prev => ({ ...prev, alunoId: '' }));
      }
    }
  };

  const exportarRelatorio = (dados, nome) => {
    relatoriosService.exportarCSV(dados, nome);
  };

  const renderDashboard = () => (
    <div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <Activity size={24} />
            </div>
          </div>
          <div className="stat-value">{estatisticas.totalEventos || 0}</div>
          <div className="stat-label">Total de Eventos</div>
        </div>
        <div className="stat-card success">
          <div className="stat-header">
            <div className="stat-icon success">
              <Users size={24} />
            </div>
          </div>
          <div className="stat-value">{estatisticas.alunosAtivos || 0}</div>
          <div className="stat-label">Alunos Ativos</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-header">
            <div className="stat-icon warning">
              <FileText size={24} />
            </div>
          </div>
          <div className="stat-value">{estatisticas.testesRealizados || 0}</div>
          <div className="stat-label">Testes Realizados</div>
        </div>
        <div className="stat-card info">
          <div className="stat-header">
            <div className="stat-icon info">
              <MessageCircle size={24} />
            </div>
          </div>
          <div className="stat-value">{estatisticas.chatsAbertos || 0}</div>
          <div className="stat-label">Chats Abertos</div>
        </div>
      </div>

      <div className="content-grid">
        <div className="content-card">
          <h3>Eventos por Categoria</h3>
          <div className="chart-placeholder">
            {Object.entries(estatisticas.eventosPorCategoria || {}).map(([categoria, count]) => (
              <div key={categoria} className="category-item">
                <span className="category-name">{categoria}</span>
                <span className="category-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="content-card">
          <h3>Atividade por Dia</h3>
          <div className="chart-placeholder">
            {Object.entries(estatisticas.eventosPorDia || {}).slice(-7).map(([data, count]) => (
              <div key={data} className="day-item">
                <span className="day-date">{new Date(data).toLocaleDateString('pt-BR')}</span>
                <span className="day-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relatorios">
      <div className="page-header">
        <h1 className="page-title">Relatórios de Atividade</h1>
        <p className="page-subtitle">Análise detalhada dos logs de atividade dos alunos</p>
      </div>

      {/* Filtros */}
      <div className="content-card filters-card">
        <div className="filters-header">
          <Filter size={20} />
          <h3>Filtros</h3>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Data Início</label>
            <input
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
            />
          </div>
          <div className="filter-group">
            <label>Data Fim</label>
            <input
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
            />
          </div>
          <div className="filter-group">
            <label>Aluno</label>
            <select
              value={filtros.alunoId}
              onChange={(e) => setFiltros({...filtros, alunoId: e.target.value})}
            >
              <option value="">Todos os alunos</option>
              {alunos.map(aluno => (
                <option key={aluno.id} value={aluno.id}>{aluno.nome}</option>
              ))}
            </select>
          </div>
          {/* Filtro de Escola - igual ao Alunos.js */}
          {!currentUserProfile?.school_adm ? (
            <div className="filter-group">
              <label>Escola</label>
              <select
                value={filtros.escolaId}
                onChange={(e) => {
                  const novaEscola = e.target.value;
                  setFiltros({...filtros, escolaId: novaEscola});
                  filtrarAlunosPorEscola(novaEscola);
                }}
              >
                <option value="">Todas as escolas</option>
                {escolas.map(escola => (
                  <option key={escola.id} value={escola.id}>{escola.nome}</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="filter-group">
              <label>Escola</label>
              <input
                type="text"
                className="form-input disabled-input"
                value={escolas.length > 0 ? escolas[0].nome : 'Escola não encontrada'}
                disabled
                style={{ backgroundColor: '#f5f5f5', color: '#666' }}
              />
            </div>
          )}
          <div className="filter-actions">
            <button className="btn btn-primary" onClick={aplicarFiltros} disabled={loading}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <BarChart3 size={16} />
            Dashboard
          </button>
          <button 
            className={`tab ${activeTab === 'testes' ? 'active' : ''}`}
            onClick={() => setActiveTab('testes')}
          >
            <FileText size={16} />
            Testes
          </button>
          <button 
            className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <MessageCircle size={16} />
            Chat
          </button>
          <button 
            className={`tab ${activeTab === 'sessoes' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessoes')}
          >
            <Users size={16} />
            Sessões
          </button>
          <button 
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            <Eye size={16} />
            Logs de Acesso
          </button>
        </div>
      </div>

      {/* Conteúdo das Tabs */}
      <div className="tab-content">
        {loading && <div className="loading">Carregando dados...</div>}
        
        {!loading && activeTab === 'dashboard' && renderDashboard()}
        
        {!loading && activeTab === 'testes' && (
          <div className="content-card">
            <div className="card-header">
              <h3>Relatório de Desempenho em Testes</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => exportarRelatorio(relatorioTestes, 'relatorio-testes')}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>Escola</th>
                    <th>Teste</th>
                    <th>Data Início</th>
                    <th>Tempo p/ Iniciar</th>
                    <th>Respostas</th>
                    <th>Navegação</th>
                    <th>Mudanças</th>
                    <th>Taxa Mudança</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorioTestes.map((item, index) => (
                    <tr key={index}>
                      <td>{item.aluno_nome}</td>
                      <td>{item.escola_nome}</td>
                      <td>{item.teste_nome}</td>
                      <td>{item.data_inicio ? new Date(item.data_inicio).toLocaleString('pt-BR') : 'N/A'}</td>
                      <td>{item.tempo_para_iniciar}ms</td>
                      <td>{item.total_respostas}</td>
                      <td>{item.total_navegacao}</td>
                      <td>{item.total_mudancas}</td>
                      <td>{item.taxa_mudanca}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && activeTab === 'chat' && (
          <div className="content-card">
            <div className="card-header">
              <h3>Relatório de Uso do Chat</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => exportarRelatorio(relatorioChat, 'relatorio-chat')}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>Escola</th>
                    <th>Sessões Chat</th>
                    <th>Matérias</th>
                    <th>Tempo Total</th>
                    <th>Tempo Médio/Sessão</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorioChat.map((item, index) => (
                    <tr key={index}>
                      <td>{item.aluno_nome}</td>
                      <td>{item.escola_nome}</td>
                      <td>{item.sessoes_chat}</td>
                      <td>{item.total_materias}</td>
                      <td>{item.tempo_total_minutos}min</td>
                      <td>{item.tempo_medio_por_sessao}min</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && activeTab === 'sessoes' && (
          <div className="content-card">
            <div className="card-header">
              <h3>Relatório de Sessões</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => exportarRelatorio(relatorioSessoes, 'relatorio-sessoes')}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>Escola</th>
                    <th>Data Início</th>
                    <th>Duração</th>
                    <th>Eventos</th>
                    <th>Dispositivo</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorioSessoes.map((item, index) => (
                    <tr key={index}>
                      <td>{item.aluno_nome}</td>
                      <td>{item.escola_nome}</td>
                      <td>{item.data_inicio ? new Date(item.data_inicio).toLocaleString('pt-BR') : 'N/A'}</td>
                      <td>{item.duracao_minutos}min</td>
                      <td>{item.total_eventos}</td>
                      <td>{item.dispositivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && activeTab === 'logs' && (
          <div className="content-card">
            <div className="card-header">
              <h3>Logs de Acesso Detalhados</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => exportarRelatorio(relatorioLogsAcesso, 'logs-acesso')}
              >
                <Download size={16} />
                Exportar CSV
              </button>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Aluno</th>
                    <th>Escola</th>
                    <th>Data</th>
                    <th>Primeiro Acesso</th>
                    <th>Último Acesso</th>
                    <th>Total Logins</th>
                    <th>Tempo Total</th>
                    <th>Status</th>
                    <th>Dispositivo</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {relatorioLogsAcesso.map((item, index) => (
                    <tr key={index}>
                      <td>{item.aluno_nome}</td>
                      <td>{item.escola_nome}</td>
                      <td>{item.data_formatada}</td>
                      <td>{item.primeiro_acesso_formatado}</td>
                      <td>{item.ultimo_acesso_formatado}</td>
                      <td>{item.total_logins}</td>
                      <td>{item.tempo_total_minutos}min</td>
                      <td>
                        <span className={`status-badge ${item.status_sessao === 'Finalizada' ? 'success' : 'warning'}`}>
                          {item.status_sessao}
                        </span>
                      </td>
                      <td>{item.dispositivo}</td>
                      <td>{item.ip_address}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .filters-card {
          margin-bottom: 20px;
        }
        
        .filters-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }
        
        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          align-items: end;
        }
        
        .filter-group label {
          display: block;
          margin-bottom: 4px;
          font-weight: 500;
        }
        
        .filter-group input,
        .filter-group select {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        
        .tabs-container {
          margin-bottom: 20px;
        }
        
        .tabs {
          display: flex;
          border-bottom: 1px solid #ddd;
        }
        
        .tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          background: none;
          cursor: pointer;
          border-bottom: 2px solid transparent;
        }
        
        .tab.active {
          border-bottom-color: #3b82f6;
          color: #3b82f6;
        }
        
        .content-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-top: 20px;
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .table-container {
          overflow-x: auto;
        }
        
        .category-item,
        .day-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #eee;
        }
        
        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .status-badge.success {
          background-color: #dcfce7;
          color: #166534;
        }
        
        .status-badge.warning {
          background-color: #fef3c7;
          color: #92400e;
        }
      `}</style>
    </div>
  );
};

export default Relatorios;
